import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Flame, Award, Users, ArrowRight, ArrowDown, Loader2 } from 'lucide-react';
import SEO from '../components/SEO';
import LazyImage from '../components/LazyImage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [parceiros, setParceiros] = useState([]);
  const [settings, setSettings] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [parceirosRes, settingsRes] = await Promise.all([
          fetch(`${API_URL}/api/public/parceiros`),
          fetch(`${API_URL}/api/public/settings`)
        ]);
        
        if (parceirosRes.ok) {
          const data = await parceirosRes.json();
          setParceiros(data);
        }
        
        if (settingsRes.ok) {
          const data = await settingsRes.json();
          setSettings(data);
        }
      } catch (err) {
        console.error('Erro ao carregar dados:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      {/* SEO */}
      {settings && (
        <SEO 
          title={settings.meta_title || 'Santa Carne - Fábrica Artesanal de Carnes Defumadas'}
          description={settings.meta_description}
          keywords={settings.meta_keywords}
          image={settings.og_image}
        />
      )}

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1674788851120-c32360e4168c?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 z-0 bg-gradient-to-b from-[#1a1a1a]/80 via-[#1a1a1a]/70 to-[#1a1a1a]" />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 z-10 text-center pt-20">
          {/* Logo */}
          <div className="mb-10">
            <img 
              src={settings?.logo_url || "https://customer-assets.emergentagent.com/job_texas-bbq-admin/artifacts/nmt6zdvd_Design%20sem%20nome.png"}
              alt={settings?.site_name || "Santa Carne BBQ"}
              className="h-48 sm:h-56 lg:h-64 w-auto mx-auto"
              loading="eager"
            />
          </div>
          
          {/* Taglines */}
          <div className="space-y-2 mb-8">
            <p className="text-xl sm:text-2xl lg:text-3xl text-[#E8DDCB] font-light italic tracking-wide" style={{fontFamily: "'Playfair Display', serif"}}>
              Respeito pelo tempo
            </p>
            <p className="text-xl sm:text-2xl lg:text-3xl text-[#E8DDCB] font-light italic tracking-wide" style={{fontFamily: "'Playfair Display', serif"}}>
              Paixão pela carne
            </p>
          </div>
          
          <div className="w-20 h-[1px] bg-gradient-to-r from-transparent via-[#B8860B] to-transparent mx-auto mb-8" />
          
          <p className="text-sm sm:text-base text-[#E8DDCB]/70 mb-12 tracking-[0.2em] uppercase">
            {settings?.site_tagline || "Fábrica artesanal de carnes defumadas"}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              size="lg"
              className="bg-[#B8860B] hover:bg-[#D4A84B] text-[#1a1a1a] font-semibold tracking-wider uppercase text-sm px-8 py-6 transition-all"
            >
              <Link to="/produtos">
                Ver Produtos
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
            <Button 
              asChild
              size="lg"
              variant="outline"
              className="border border-[#E8DDCB]/30 text-[#E8DDCB] hover:bg-[#E8DDCB]/10 hover:border-[#E8DDCB] font-semibold tracking-wider uppercase text-sm px-8 py-6 transition-all"
            >
              <a href="#quem-somos">Quem Somos</a>
            </Button>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-10">
          <div className="w-[1px] h-16 bg-gradient-to-b from-[#B8860B] to-transparent animate-pulse" />
        </div>
      </section>

      {/* Sobre a Santa Carne */}
      <section id="quem-somos" className="py-24 bg-[#1a1a1a]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div>
                <p className="text-[#B8860B] text-sm tracking-[0.3em] uppercase mb-4">Sobre Nós</p>
                <h2 className="text-4xl lg:text-5xl font-semibold text-[#E8DDCB] leading-tight" style={{fontFamily: "'Playfair Display', serif"}}>
                  Quem Somos
                </h2>
                <div className="w-16 h-[2px] bg-[#B8860B] mt-6" />
              </div>
              
              <p className="text-[#E8DDCB]/80 text-lg leading-relaxed">
                Na Santa Carne, o tempo é o nosso maior ingrediente. Cada peça nasce com propósito: selecionamos os melhores cortes, acendemos a lenha certa e deixamos que a defumação lenta revele o seu carácter.
              </p>
              <p className="text-[#E8DDCB]/80 text-lg leading-relaxed">
                Unimos tradição portuguesa, alma brasileira e técnica americana num processo artesanal onde cada detalhe importa.
              </p>
              <p className="text-[#B8860B] text-xl font-medium italic" style={{fontFamily: "'Playfair Display', serif"}}>
                "Santa Carne é sabor com identidade: feito devagar, feito com alma."
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-6">
                <div className="text-center p-6 border border-[#B8860B]/20 rounded-lg hover:border-[#B8860B]/50 transition-all">
                  <Award className="w-8 h-8 text-[#B8860B] mx-auto mb-3" />
                  <h3 className="text-[#E8DDCB] font-medium text-sm">Premium</h3>
                  <p className="text-[#E8DDCB]/60 text-xs mt-1">Cortes selecionados</p>
                </div>
                <div className="text-center p-6 border border-[#B8860B]/20 rounded-lg hover:border-[#B8860B]/50 transition-all">
                  <Flame className="w-8 h-8 text-[#B8860B] mx-auto mb-3" />
                  <h3 className="text-[#E8DDCB] font-medium text-sm">Artesanal</h3>
                  <p className="text-[#E8DDCB]/60 text-xs mt-1">Defumação lenta</p>
                </div>
                <div className="text-center p-6 border border-[#B8860B]/20 rounded-lg hover:border-[#B8860B]/50 transition-all">
                  <Users className="w-8 h-8 text-[#B8860B] mx-auto mb-3" />
                  <h3 className="text-[#E8DDCB] font-medium text-sm">Parceiros</h3>
                  <p className="text-[#E8DDCB]/60 text-xs mt-1">B2B & Eventos</p>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <LazyImage
                src="https://images.unsplash.com/photo-1614231555937-aaf95bb2a383?w=800"
                alt="BBQ Santa Carne"
                className="w-full h-[500px] rounded-lg"
              />
              <div className="absolute -bottom-4 -right-4 w-full h-full border border-[#B8860B]/30 rounded-lg -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Parceiros */}
      <section className="py-24 bg-[#242424]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#B8860B] text-sm tracking-[0.3em] uppercase mb-4">Confiança</p>
            <h2 className="text-4xl lg:text-5xl font-semibold text-[#E8DDCB]" style={{fontFamily: "'Playfair Display', serif"}}>
              Nossos Parceiros
            </h2>
            <div className="w-16 h-[2px] bg-[#B8860B] mx-auto mt-6" />
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 text-[#B8860B] animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
              {parceiros.map((parceiro) => (
                <div 
                  key={parceiro.id}
                  className="bg-[#1a1a1a] rounded-lg p-8 flex items-center justify-center border border-[#B8860B]/10 hover:border-[#B8860B]/40 transition-all"
                >
                  <img 
                    src={parceiro.logo} 
                    alt={parceiro.nome}
                    className="max-w-full h-auto opacity-80 hover:opacity-100 transition-opacity"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          )}
          
          <div className="text-center">
            <Button 
              asChild
              size="lg"
              className="bg-transparent border border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-[#1a1a1a] font-semibold tracking-wider uppercase text-sm px-8 py-6 transition-all"
            >
              <Link to="/parceiros">Seja Parceiro</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Catálogo */}
      <section className="py-24 bg-[#1a1a1a] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-[#B8860B] rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] border border-[#B8860B] rounded-full" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <p className="text-[#B8860B] text-sm tracking-[0.3em] uppercase">Download</p>
            <h2 className="text-4xl lg:text-5xl font-semibold text-[#E8DDCB]" style={{fontFamily: "'Playfair Display', serif"}}>
              Catálogo Completo
            </h2>
            <p className="text-[#E8DDCB]/70 text-lg max-w-xl mx-auto">
              Para restaurantes, talhos e eventos. Descubra toda a nossa gama de produtos premium.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild
                size="lg"
                className="bg-[#B8860B] hover:bg-[#D4A84B] text-[#1a1a1a] font-semibold tracking-wider uppercase text-sm px-10 py-6 transition-all"
              >
                <Link to="/catalogo">
                  <ArrowDown className="mr-2 w-4 h-4" />
                  Download PDF
                </Link>
              </Button>
              <Button 
                asChild
                size="lg"
                variant="outline"
                className="border border-[#E8DDCB]/30 text-[#E8DDCB] hover:bg-[#E8DDCB]/10 hover:border-[#E8DDCB] font-semibold tracking-wider uppercase text-sm px-10 py-6 transition-all"
              >
                <Link to="/catalogo">
                  Ver Catálogo
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
